import PhoneIphoneOutlined from '@mui/icons-material/PhoneIphoneOutlined';
import PhonelinkSetupOutlined from '@mui/icons-material/PhonelinkSetupOutlined';
import WatchLaterOutlined from '@mui/icons-material/WatchLaterOutlined';
import { Box, Chip, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { NfcCard, Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { orderBy, upperFirst } from 'lodash';
import { useMemo } from 'react';
import {
  getCardsByVisitorId,
  getStatusPriority as getStatusPriorityNfcCard,
  isCardBlockedStatus,
  isCardExpiredStatus,
  isCardExpiresSoonStatus,
  isRightsChangedStatus,
  isUserExpiresSoonStatus
} from 'utils/NfcCard/nfcCard';
import {
  getStatusPriority,
  getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers,
  getUserDeletionDate,
  getVisibleVisitorGroupMemberships,
  getVisitorMembershipByVisitorGroupId,
  getVisitorStatuses,
  Statuses,
  translateRole
} from 'utils/Visitor/visitor';
import CardChip, { CardChipColor } from 'v2/components/CardChip/CardChip';
import { CardColumnHeader } from 'v2/components/CardColumnHeader/CardColumnHeader';
import ChipListCell from 'v2/components/ChipListCell/ChipListCell';
import { ColumnHeaderWithTooltip } from 'v2/components/ColumnHeaderWithTooltip/ColumnHeaderWithTooltip';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import { ExpirationColumnHeader } from 'v2/components/ExpirationCell/ExpirationColumnHeader';
import GroupAvatarCell from 'v2/components/GroupAvatarCell/GroupAvatarCell';
import { OriginCell } from 'v2/components/OriginCell/OriginCell';
import { OriginColumnHeader } from 'v2/components/OriginColumnHeader/OriginColumnHeader';
import RepeatOverflowCounter from 'v2/components/RepeatOverflowCounter/RepeatOverflowCounter';
import { RoleColumnHeader } from 'v2/components/RoleColumnHeader/RoleColumnHeader';
import StatusChip from 'v2/components/StatusChip/StatusChip';
import { StatusColumnHeader } from 'v2/components/StatusColumnHeader/StatusColumnHeader';
import { TwoLineCell } from 'v2/components/TextCell/TextCell';
import { WarningCell } from 'v2/components/Warning/Warning';
import { EMPTY_VALUE_PLACEHOLDER } from 'v2/constants';
import { formatDate } from 'v2/utils/dateTime';
import { getVisitorOriginLabel } from 'v2/utils/visitorOrigin';

export type AllUsersOverviewVariant = 'Page' | 'Tab';

export interface AllUsersOverviewRow {
  id: string;
  visitor: Visitor;
}

interface Props {
  cards: NfcCard[];
  variant: AllUsersOverviewVariant;
  groupId?: string;
}

const getGroupExpirationDate = (visitor: Visitor, groupId: string) =>
  getVisitorMembershipByVisitorGroupId(visitor, groupId)?.expirationDate ?? null;

const getGroupsCell = (visitor: Visitor, translation: Translation) => {
  const groups = getVisibleVisitorGroupMemberships(visitor).map((membership) => ({
    id: membership.visitorGroup.id,
    name: membership.visitorGroup.name
  }));
  if (groups.length === 0) return <WarningCell label={translation.no_groups_assigned} />;
  return <GroupAvatarCell groups={groups} />;
};

const getRolesItems = (visitor: Visitor, translation: Translation) =>
  getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers(visitor).map((role) => ({
    id: role,
    name: upperFirst(translateRole(role, translation))
  }));

const getCardChipColor = (card: NfcCard): CardChipColor => {
  if (isCardBlockedStatus(card)) return 'secondary';
  if (isCardExpiredStatus(card) || isRightsChangedStatus(card)) return 'error';
  if (isCardExpiresSoonStatus(card) || isUserExpiresSoonStatus(card)) return 'warning';
  return 'success';
};

const getCardsCell = (visitor: Visitor, cards: NfcCard[]) => {
  const cardsAssignedToVisitor = getCardsByVisitorId(visitor.id, cards);
  const cardsAssignedToVisitorWithStatusPriority = cardsAssignedToVisitor.map((card) => ({
    ...card,
    statusPriority: getStatusPriorityNfcCard(card)
  }));
  const sortedCardsAssignedToVisitorByStatusPriority = orderBy(
    cardsAssignedToVisitorWithStatusPriority,
    'statusPriority'
  );

  return (
    <Box height="52px" display="flex" alignItems="center">
      <RepeatOverflowCounter
        variant="chip"
        renderMore={(count) => (
          <Chip
            label={<Typography variant="caption">{`+${count}`}</Typography>}
            variant="filled"
            size="small"
            color="secondary"
          />
        )}
      >
        {sortedCardsAssignedToVisitorByStatusPriority.map((card) => (
          <CardChip key={card.uuid} label={card.name} color={getCardChipColor(card)} />
        ))}
      </RepeatOverflowCounter>
    </Box>
  );
};

const getVisitorStatusChips = (visitor: Visitor, cards: NfcCard[], translation: Translation, iconOnly: boolean) => {
  const statuses = getVisitorStatuses(visitor, cards);
  switch (statuses) {
    case Statuses.isConfigDeviceDisconnected:
      return (
        <StatusChip
          color="error"
          icon={PhonelinkSetupOutlined}
          label={translation.disconnected}
          tooltip={translation.access_disconnected_tooltip_config}
          iconOnly={iconOnly}
        />
      );
    case Statuses.isAccessDeviceDisconnectedAndVisitorHasNoCards:
      return (
        <StatusChip
          color="error"
          icon={PhoneIphoneOutlined}
          label={translation.disconnected}
          tooltip={translation.access_disconnected_tooltip_no_card}
          iconOnly={iconOnly}
        />
      );
    case Statuses.isAccessDeviceDisconnectedAndVisitorHasCards:
      return (
        <StatusChip
          color="warning"
          icon={PhoneIphoneOutlined}
          label={translation.disconnected}
          tooltip={translation.access_disconnected_tooltip_has_card}
          iconOnly={iconOnly}
        />
      );
    case Statuses.isVisitorExpireIn24H:
      return (
        <StatusChip
          color="error"
          icon={WatchLaterOutlined}
          label={upperFirst(translation.expires_in_24h)}
          iconOnly={iconOnly}
        />
      );
    case Statuses.isVisitorExpireIn1Week:
      return (
        <StatusChip
          color="warning"
          icon={WatchLaterOutlined}
          label={upperFirst(translation.expires_this_week)}
          iconOnly={iconOnly}
        />
      );
    default:
      return (
        <StatusChip color="success" icon={PhoneIphoneOutlined} label={translation.connected} iconOnly={iconOnly} />
      );
  }
};

const useAllUsersColumns = ({ cards, variant, groupId }: Props) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const isMediumAndAbove = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeAndAbove = useMediaQuery(theme.breakpoints.up('lg'));
  const isExtraLargeAndAbove = useMediaQuery(theme.breakpoints.up('xl'));

  const columns = useMemo<GridColDef<AllUsersOverviewRow>[]>(() => {
    const headerWithId = renderHeaderWithId();

    return [
      {
        field: 'user',
        headerName: translation.user,
        sortable: true,
        flex: 3,
        minWidth: 120,
        renderHeader: headerWithId,
        valueGetter: (_, row) => row.visitor.name,
        renderCell: (params) => {
          const { visitor } = params.row;
          return <TwoLineCell title={visitor.name} content={visitor.email} />;
        }
      },
      {
        field: 'origin',
        headerName: translation.origin,
        sortable: true,
        width: 110,
        valueGetter: (_, row) => getVisitorOriginLabel(row.visitor, translation),
        renderHeader: renderHeaderWithId(undefined, () => <OriginColumnHeader />),
        renderCell: (params) => {
          const { visitor } = params.row;
          return <OriginCell label={getVisitorOriginLabel(visitor, translation)} />;
        }
      },
      {
        field: 'groups',
        headerName: translation.user_groups,
        width: 170,
        sortable: false,
        renderHeader: renderHeaderWithId(undefined, () => (
          <ColumnHeaderWithTooltip
            label={translation.user_groups}
            tooltip={
              <Stack spacing={1}>
                <Typography variant="caption">{translation.user_groups_tooltip_multiple_membership}</Typography>
                <Typography variant="caption">{translation.user_groups_tooltip_admin_installer_roles}</Typography>
              </Stack>
            }
          />
        )),
        renderCell: (params) => {
          const { visitor } = params.row;
          return getGroupsCell(visitor, translation);
        }
      },
      {
        field: 'role',
        headerName: translation.role_or_roles,
        sortable: false,
        minWidth: 150,
        flex: 1,
        renderHeader: renderHeaderWithId(undefined, () => <RoleColumnHeader />),
        renderCell: (params) => {
          const { visitor } = params.row;
          return <ChipListCell items={getRolesItems(visitor, translation)} />;
        }
      },
      {
        field: 'expiration',
        headerName: translation.expiration,
        sortable: true,
        valueGetter: (_, row) =>
          groupId ? getGroupExpirationDate(row.visitor, groupId) : getUserDeletionDate(row.visitor),
        renderHeader: renderHeaderWithId(undefined, () => <ExpirationColumnHeader showTooltip={!groupId} />),
        minWidth: 160,
        renderCell: (params) => {
          const expirationDate = groupId
            ? getGroupExpirationDate(params.row.visitor, groupId)
            : getUserDeletionDate(params.row.visitor);
          if (!expirationDate) return EMPTY_VALUE_PLACEHOLDER;
          return (
            <TwoLineCell
              title={formatDate(expirationDate, 'dateOnly')}
              content={formatDate(expirationDate, 'timeOnly')}
            />
          );
        }
      },
      {
        field: 'card',
        headerName: translation.card,
        sortable: false,
        flex: 1,
        minWidth: 80,
        renderHeader: renderHeaderWithId(undefined, () => <CardColumnHeader />),
        renderCell: (params) => {
          const { visitor } = params.row;
          return getCardsCell(visitor, cards);
        }
      },
      {
        field: 'status',
        headerName: translation.status,
        sortable: true,
        valueGetter: (_, row) => getStatusPriority(row.visitor, cards),
        width: isExtraLargeAndAbove ? 160 : 120,
        renderHeader: renderHeaderWithId(undefined, () => <StatusColumnHeader />),
        renderCell: (params) => {
          const { visitor } = params.row;
          return getVisitorStatusChips(visitor, cards, translation, !isExtraLargeAndAbove);
        }
      }
    ];
  }, [cards, groupId, isExtraLargeAndAbove, translation]);

  const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(
    () =>
      variant === 'Page'
        ? {
            user: true,
            origin: isLargeAndAbove,
            groups: isLargeAndAbove,
            role: isLargeAndAbove,
            expiration: isMediumAndAbove,
            card: isExtraLargeAndAbove,
            status: true
          }
        : {
            user: true,
            origin: false,
            groups: false,
            role: isMediumAndAbove,
            expiration: isMediumAndAbove,
            card: isMediumAndAbove,
            status: true
          },
    [isExtraLargeAndAbove, isLargeAndAbove, isMediumAndAbove, variant]
  );

  return { columns, columnVisibilityModel };
};

export default useAllUsersColumns;
